import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { Campaign, CampaignLaunchMethod, CampaignStatus } from '../models/campaign';

@Injectable({ providedIn: 'root' })
export class CampaignService {
    private campaignSubject: BehaviorSubject<Campaign>;

    constructor(
        private http: HttpClient,
        private authenticationService: AuthenticationService
    ) {
        this.campaignSubject = new BehaviorSubject<Campaign>(null);
    }

    public cachedHighlights: Map<string, any> = new Map();

    public get getCampaignValue(): Campaign {
        return this.campaignSubject.value;
    }

    public getCampaignSubject() {
        return this.campaignSubject;
    }

    create(
        title: string,
        brand: string,
        budget: number,
        billingMethod: string,
        industryId: string,
        issuerName: string,
        issuerEmail: string,
        termsAccepted: boolean
    ) {
        return this.http
            .post<any>(`${environment.apiUrl}/campaign/`, {
                title,
                brand,
                budget,
                billingMethod,
                industryId,
                issuerName,
                issuerEmail,
                termsAccepted
            })
            .pipe(
                tap(campaign => {
                    this.campaignSubject.next(campaign);
                    return campaign;
                })
            );
    }

    getById(id: string, withChannelDetails = false) {
        const query = withChannelDetails ? '?withChannelDetails=true' : '';
        return this.http
            .get<Campaign>(
                `${environment.apiUrl
                }/campaign/${id}/${this.authenticationService.getSessionRole()}${query}`
            )
            .pipe(
                tap(campaign => {
                    this.campaignSubject.next(campaign);
                    return campaign;
                })
            );
    }

    startsInLessThan2WorkingDays(id: string) {
        return this.http
            .get<boolean>(`${environment.apiUrl}/campaign/${id}/starts-in-less-than-2-working-days`)
            .pipe(
                tap(result => {
                    return result;
                })
            );
    }

    update(id, params) {
        const activeRole = this.authenticationService.getSessionRole();
        return this.http.put(`${environment.apiUrl}/campaign/${id}/${activeRole}`, params).pipe(
            tap((campaign: Campaign) => {
                this.campaignSubject.next(campaign);
                return campaign;
            })
        );
    }

    /** Slim update for the Step 1 information fields only (no products/channels/dates payload). */
    updateInformation(id, params) {
        const activeRole = this.authenticationService.getSessionRole();
        return this.http
            .put<Campaign>(`${environment.apiUrl}/campaign/information/${id}/${activeRole}`, params)
            .pipe(tap((campaign: Campaign) => this.campaignSubject.next(campaign)));
    }

    addChannel(campaignId: any, channelId: number) {
        const activeRole = this.authenticationService.getSessionRole();
        return this.http.post<Campaign>(`${environment.apiUrl}/campaign/location/${campaignId}/channels/add/${channelId}/${activeRole}`,{}).pipe(
            tap((campaign: Campaign) => this.campaignSubject.next(campaign))
        );
    }

    removeChannel(campaignId: any, channelId: number) {
        const activeRole = this.authenticationService.getSessionRole();
        return this.http.put<Campaign>(`${environment.apiUrl}/campaign/location/${campaignId}/channels/remove/${channelId}/${activeRole}`,{}).pipe(
            tap((campaign: Campaign) => this.campaignSubject.next(campaign))
        );
    }

    updateNumberOfVisuals(id, params) {
        return this.http.put(`${environment.apiUrl}/campaign/number-of-visuals/${id}`, params);
    }

    deleteCampaign(id) {
        return this.http.delete(`${environment.apiUrl}/campaign/${id}`);
    }

    relaunchCampaign(id, upcomingStatusDate = null) {
        return this.http.put(`${environment.apiUrl}/campaign/relaunch/${id}`, {
            upcomingStatusDate
        });
    }

    updateCampaignStatus(id: string, status: CampaignStatus, upcomingStatusDate: Date = null) {
        return this.http.put(`${environment.apiUrl}/campaign/status/${id}`, {
            status,
            upcomingStatusDate,
            activeRole: this.authenticationService.getSessionRole()
        });
    }

    restorePreviousCampaignState(id: string) {
        return this.http.put(`${environment.apiUrl}/campaign/restore-previous-state/${id}`, {});
    }

    updateCampaignLaunchMethod(id: string, launchMethod: CampaignLaunchMethod) {
        return this.http.put(`${environment.apiUrl}/campaign/launch-method/${id}`, {
            launchMethod
        });
    }

    dismissBanner(campaignId: string, type: string) {
        return this.http.post(`${environment.apiUrl}/campaign/${campaignId}/dismiss-banner/${type}`, {});
    }

    destroyCampaign() {
        this.campaignSubject.next(null);
    }

    getCampaigns(params) {
        params.activeRole = this.authenticationService.getSessionRole();
        const isClient = params.activeRole === 'CLIENT';
        const isDefaultSort = params.sortColumn === 'modifiedAt' || params.sortColumn === 'updatedAt';
        if (isClient && isDefaultSort) {
            params.sortColumn = 'clientUpdatedAt';
        }
        return this.http.post(`${environment.apiUrl}/campaign/search`, params);
    }

    getCampaignMetrics(campaign) {
        return this.http.post(`${environment.apiUrl}/campaign/metrics/calculate`, {
            campaignDTO: campaign,
            activeRole: this.authenticationService.getSessionRole()
        });
    }

    /**
     * The provider forecast broken down per channel, per city and by frequency, for the Performance
     * Insights panel. Posts the campaign as it currently stands so unsaved selections are included,
     * exactly as the metrics call does.
     */
    getCampaignPerformanceInsights(campaign) {
        return this.http.post(`${environment.apiUrl}/campaign/metrics/performance-insights`, {
            campaignDTO: campaign,
            activeRole: this.authenticationService.getSessionRole()
        });
    }

    /**
     * The same breakdown for a campaign that has already run, looked up by id rather than posted:
     * what a live or finished campaign delivered is a fact about what ran, not about the selections
     * currently on screen. Drives the DOOH Campaign Results dashboard.
     */
    getCampaignDeliveredInsights(campaignId: string, selectedChannelId: number = null) {
        const channel = selectedChannelId == null ? '' : `?selectedChannelId=${selectedChannelId}`;
        return this.http.get(
            `${environment.apiUrl}/campaign/metrics/post-campaign-insights/${campaignId}${channel}`
        );
    }

    /**
     * A saved campaign's metrics by id. Read by the analytics page to learn which source answers for
     * this campaign — the flag is derived from the campaign's billing method and the platform's
     * configured source, so it does not change with the provider's health.
     */
    getCampaignMetricsById(campaignId: string) {
        return this.http.get(
            `${environment.apiUrl}/campaign/metrics/${campaignId}` +
            `?activeRole=${this.authenticationService.getSessionRole()}`
        );
    }

    getCampaignSummary(id) {
        return this.http.get(
            `${environment.apiUrl
            }/campaign/${id}/summary/${this.authenticationService.getSessionRole()}`
        );
    }

    rejectAdminCampaign(id, reason) {
        return this.http.post<any>(`${environment.apiUrl}/campaign/admin/reject-campaign/${id}`, {
            rejectReason: reason
        });
    }

    approveAdminCampaign(id, reason) {
        return this.http.post<any>(
            `${environment.apiUrl}/campaign/admin/approve-campaign/${id}`,
            null
        );
    }
}
